import type { LocaleContentMap } from '../i18n.config';

export interface MarketFitContent {
  badge: string;
  title: string;
  subtitle: string;
  headers: { segment: string; fitLevel: string; capabilities: string };
  fitLabels: { excellent: string; strong: string; good: string };
  segments: Array<{ segment: string; fit: 'excellent' | 'strong' | 'good'; note: string }>;
}

export type MarketFitContentMap = LocaleContentMap<MarketFitContent>;

const EN: MarketFitContent = {
  badge: 'Who Is HotelStack For?',
  title: 'Target Market Fit',
  subtitle: 'From boutique B&Bs to global hotel chains — HotelStack adapts to every property type and market.',
  headers: { segment: 'Market Segment', fitLevel: 'Fit Level', capabilities: 'Key Capabilities' },
  fitLabels: { excellent: 'Excellent Fit', strong: 'Strong Fit', good: 'Good Fit' },
  segments: [
    { segment: 'Small Hotels (1–20 rooms)', fit: 'excellent', note: 'Kiosk check-in, simplified PMS, affordable pricing' },
    { segment: 'Mid-Size Hotels (21–100 rooms)', fit: 'excellent', note: 'Full PMS suite, channel manager, revenue tools' },
    { segment: 'Large Hotels (100+ rooms)', fit: 'excellent', note: 'Enterprise dashboards, night audit, advanced analytics' },
    { segment: 'Hotel Chains (Multi-Property)', fit: 'excellent', note: 'Chain dashboard, cross-property analytics, centralized rates' },
    { segment: 'Boutique & Lifestyle', fit: 'strong', note: 'Guest CRM, personalized communications, loyalty program' },
    { segment: 'Resorts & Spas', fit: 'strong', note: 'POS, F&B management, activity booking, group reservations' },
    { segment: 'Hostels & Budget', fit: 'good', note: 'Room board, basic PMS, OTA channel sync' },
    { segment: 'B&Bs & Vacation Rentals', fit: 'good', note: 'Booking engine, guest messaging, invoice management' },
    { segment: 'Indian Market', fit: 'excellent', note: 'GST compliance, C-Form, FRRO, UPI payments, Goibibo/MakeMyTrip' },
    { segment: 'Global Market', fit: 'excellent', note: '6+ currencies, multi-language, GDPR, 200+ OTA channels, GDS' },
  ],
};

export const MARKET_FIT_CONTENT: MarketFitContentMap = {
  en: EN,
  ar: {
    badge: 'لمن صُمم HotelStack؟',
    title: 'ملاءمة السوق المستهدف',
    subtitle: 'من الفنادق الصغيرة إلى سلاسل الفنادق العالمية — HotelStack يتكيف مع كل نوع من العقارات والأسواق.',
    headers: { segment: 'شريحة السوق', fitLevel: 'مستوى الملاءمة', capabilities: 'القدرات الرئيسية' },
    fitLabels: { excellent: 'ملاءمة ممتازة', strong: 'ملاءمة قوية', good: 'ملاءمة جيدة' },
    segments: [
      { segment: 'فنادق صغيرة (١–٢٠ غرفة)', fit: 'excellent', note: 'تسجيل ذاتي، نظام PMS مبسط، أسعار مناسبة' },
      { segment: 'فنادق متوسطة (٢١–١٠٠ غرفة)', fit: 'excellent', note: 'مجموعة PMS كاملة، مدير قنوات، أدوات إيرادات' },
      { segment: 'فنادق كبيرة (١٠٠+ غرفة)', fit: 'excellent', note: 'لوحات تحكم مؤسسية، تدقيق ليلي، تحليلات متقدمة' },
      { segment: 'سلاسل فنادق (متعددة العقارات)', fit: 'excellent', note: 'لوحة السلسلة، تحليلات عبر العقارات، أسعار مركزية' },
      { segment: 'فنادق بوتيك وأسلوب حياة', fit: 'strong', note: 'إدارة علاقات الضيوف، اتصالات مخصصة، برنامج ولاء' },
      { segment: 'منتجعات ومنتجعات صحية', fit: 'strong', note: 'نقاط بيع، إدارة أغذية ومشروبات، حجز أنشطة' },
      { segment: 'نزل واقتصادي', fit: 'good', note: 'لوحة غرف، PMS أساسي، مزامنة قنوات OTA' },
      { segment: 'بيوت ضيافة وإيجارات عطلات', fit: 'good', note: 'محرك حجز، رسائل ضيوف، إدارة فواتير' },
      { segment: 'السوق الهندي', fit: 'excellent', note: 'امتثال GST، نموذج C، FRRO، مدفوعات UPI' },
      { segment: 'السوق العالمي', fit: 'excellent', note: '٦+ عملات، متعدد اللغات، GDPR، ٢٠٠+ قناة OTA، GDS' },
    ],
  },
  th: {
    badge: 'HotelStack เหมาะกับใคร?',
    title: 'ความเหมาะสมกับตลาด',
    subtitle: 'จากบูทีคโฮเต็ลไปจนถึงเครือโรงแรมระดับโลก — HotelStack ปรับตัวได้กับทุกประเภทและทุกตลาด',
    headers: { segment: 'กลุ่มตลาด', fitLevel: 'ระดับความเหมาะสม', capabilities: 'ความสามารถหลัก' },
    fitLabels: { excellent: 'เหมาะสมอย่างยิ่ง', strong: 'เหมาะสมมาก', good: 'เหมาะสมดี' },
    segments: [
      { segment: 'โรงแรมขนาดเล็ก (1–20 ห้อง)', fit: 'excellent', note: 'คีออสก์เช็คอิน, PMS แบบง่าย, ราคาย่อมเยา' },
      { segment: 'โรงแรมขนาดกลาง (21–100 ห้อง)', fit: 'excellent', note: 'ชุด PMS เต็มรูปแบบ, ตัวจัดการช่องทาง, เครื่องมือรายได้' },
      { segment: 'โรงแรมขนาดใหญ่ (100+ ห้อง)', fit: 'excellent', note: 'แดชบอร์ดองค์กร, ตรวจสอบกลางคืน, วิเคราะห์ขั้นสูง' },
      { segment: 'เครือโรงแรม (หลายสาขา)', fit: 'excellent', note: 'แดชบอร์ดเครือ, วิเคราะห์ข้ามสาขา, อัตราส่วนกลาง' },
      { segment: 'บูทีคและไลฟ์สไตล์', fit: 'strong', note: 'CRM สำหรับแขก, การสื่อสารเฉพาะบุคคล, โปรแกรมสะสมแต้ม' },
      { segment: 'รีสอร์ทและสปา', fit: 'strong', note: 'POS, จัดการอาหารเครื่องดื่ม, จองกิจกรรม' },
      { segment: 'โฮสเทลและประหยัด', fit: 'good', note: 'บอร์ดห้อง, PMS พื้นฐาน, ซิงค์ช่องทาง OTA' },
      { segment: 'B&B และเช่าวันหยุด', fit: 'good', note: 'ระบบจอง, ส่งข้อความแขก, จัดการใบแจ้งหนี้' },
      { segment: 'ตลาดอินเดีย', fit: 'excellent', note: 'GST, C-Form, FRRO, UPI, Goibibo/MakeMyTrip' },
      { segment: 'ตลาดทั่วโลก', fit: 'excellent', note: '6+ สกุลเงิน, หลายภาษา, GDPR, 200+ ช่องทาง OTA, GDS' },
    ],
  },
  es: {
    badge: '¿Para quién es HotelStack?',
    title: 'Ajuste al Mercado Objetivo',
    subtitle: 'Desde B&Bs boutique hasta cadenas hoteleras globales — HotelStack se adapta a cada tipo de propiedad y mercado.',
    headers: { segment: 'Segmento de Mercado', fitLevel: 'Nivel de Ajuste', capabilities: 'Capacidades Clave' },
    fitLabels: { excellent: 'Ajuste Excelente', strong: 'Ajuste Fuerte', good: 'Buen Ajuste' },
    segments: [
      { segment: 'Hoteles pequeños (1–20 habitaciones)', fit: 'excellent', note: 'Check-in en quiosco, PMS simplificado, precios accesibles' },
      { segment: 'Hoteles medianos (21–100 habitaciones)', fit: 'excellent', note: 'Suite PMS completa, gestor de canales, herramientas de ingresos' },
      { segment: 'Hoteles grandes (100+ habitaciones)', fit: 'excellent', note: 'Paneles empresariales, auditoría nocturna, analíticas avanzadas' },
      { segment: 'Cadenas hoteleras (Multi-Propiedad)', fit: 'excellent', note: 'Panel de cadena, analíticas cruzadas, tarifas centralizadas' },
      { segment: 'Boutique y Estilo de vida', fit: 'strong', note: 'CRM de huéspedes, comunicaciones personalizadas, programa de fidelidad' },
      { segment: 'Resorts y Spas', fit: 'strong', note: 'POS, gestión de F&B, reservas de actividades, reservas grupales' },
      { segment: 'Hostales y Económicos', fit: 'good', note: 'Tablero de habitaciones, PMS básico, sincronización OTA' },
      { segment: 'B&Bs y Alquileres vacacionales', fit: 'good', note: 'Motor de reservas, mensajería, gestión de facturas' },
      { segment: 'Mercado indio', fit: 'excellent', note: 'Cumplimiento GST, C-Form, FRRO, pagos UPI' },
      { segment: 'Mercado global', fit: 'excellent', note: '6+ monedas, multilingüe, GDPR, 200+ canales OTA, GDS' },
    ],
  },
  fr: {
    badge: 'Pour qui est HotelStack ?',
    title: 'Adéquation au Marché Cible',
    subtitle: 'Des B&Bs de charme aux chaînes hôtelières mondiales — HotelStack s\'adapte à chaque type de propriété et de marché.',
    headers: { segment: 'Segment de Marché', fitLevel: 'Niveau d\'Adéquation', capabilities: 'Capacités Clés' },
    fitLabels: { excellent: 'Excellent', strong: 'Très Bon', good: 'Bon' },
    segments: [
      { segment: 'Petits hôtels (1–20 chambres)', fit: 'excellent', note: 'Enregistrement kiosque, PMS simplifié, tarifs abordables' },
      { segment: 'Hôtels moyens (21–100 chambres)', fit: 'excellent', note: 'Suite PMS complète, gestionnaire de canaux, outils revenus' },
      { segment: 'Grands hôtels (100+ chambres)', fit: 'excellent', note: 'Tableaux de bord entreprise, audit de nuit, analytique avancée' },
      { segment: 'Chaînes hôtelières (Multi-Propriétés)', fit: 'excellent', note: 'Tableau de bord chaîne, analytique multi-propriétés, tarifs centralisés' },
      { segment: 'Boutique et Lifestyle', fit: 'strong', note: 'CRM clients, communications personnalisées, programme de fidélité' },
      { segment: 'Resorts et Spas', fit: 'strong', note: 'POS, gestion F&B, réservation d\'activités, réservations de groupes' },
      { segment: 'Auberges et Budget', fit: 'good', note: 'Tableau des chambres, PMS de base, synchronisation OTA' },
      { segment: 'Chambres d\'hôtes et Locations', fit: 'good', note: 'Moteur de réservation, messagerie, gestion de factures' },
      { segment: 'Marché indien', fit: 'excellent', note: 'Conformité GST, C-Form, FRRO, paiements UPI' },
      { segment: 'Marché mondial', fit: 'excellent', note: '6+ devises, multilingue, RGPD, 200+ canaux OTA, GDS' },
    ],
  },
  de: {
    badge: 'Für wen ist HotelStack?',
    title: 'Zielmarkt-Eignung',
    subtitle: 'Von Boutique-B&Bs bis zu globalen Hotelketten — HotelStack passt sich jedem Immobilientyp und Markt an.',
    headers: { segment: 'Marktsegment', fitLevel: 'Eignungsgrad', capabilities: 'Kernfähigkeiten' },
    fitLabels: { excellent: 'Ausgezeichnet', strong: 'Stark', good: 'Gut' },
    segments: [
      { segment: 'Kleine Hotels (1–20 Zimmer)', fit: 'excellent', note: 'Kiosk-Check-in, vereinfachtes PMS, günstige Preise' },
      { segment: 'Mittelgroße Hotels (21–100 Zimmer)', fit: 'excellent', note: 'Vollständige PMS-Suite, Channel Manager, Revenue-Tools' },
      { segment: 'Große Hotels (100+ Zimmer)', fit: 'excellent', note: 'Enterprise-Dashboards, Nachtaudit, erweiterte Analytik' },
      { segment: 'Hotelketten (Multi-Property)', fit: 'excellent', note: 'Ketten-Dashboard, übergreifende Analytik, zentrale Tarife' },
      { segment: 'Boutique & Lifestyle', fit: 'strong', note: 'Gäste-CRM, personalisierte Kommunikation, Treueprogramm' },
      { segment: 'Resorts & Spas', fit: 'strong', note: 'POS, F&B-Management, Aktivitätsbuchung, Gruppenbuchungen' },
      { segment: 'Hostels & Budget', fit: 'good', note: 'Zimmertafel, Basis-PMS, OTA-Channel-Sync' },
      { segment: 'B&Bs & Ferienwohnungen', fit: 'good', note: 'Buchungsmaschine, Gästenachrichten, Rechnungsverwaltung' },
      { segment: 'Indischer Markt', fit: 'excellent', note: 'GST-Compliance, C-Form, FRRO, UPI-Zahlungen' },
      { segment: 'Globaler Markt', fit: 'excellent', note: '6+ Währungen, mehrsprachig, DSGVO, 200+ OTA-Kanäle, GDS' },
    ],
  },
  ja: {
    badge: 'HotelStackは誰のため？',
    title: 'ターゲット市場適合性',
    subtitle: 'ブティックB&Bからグローバルホテルチェーンまで — HotelStackはあらゆる物件タイプと市場に適応します。',
    headers: { segment: '市場セグメント', fitLevel: '適合レベル', capabilities: '主要機能' },
    fitLabels: { excellent: '最適', strong: '非常に適合', good: '適合' },
    segments: [
      { segment: '小規模ホテル（1–20室）', fit: 'excellent', note: 'キオスクチェックイン、シンプルPMS、手頃な価格' },
      { segment: '中規模ホテル（21–100室）', fit: 'excellent', note: 'フルPMSスイート、チャネルマネージャー、収益ツール' },
      { segment: '大規模ホテル（100室以上）', fit: 'excellent', note: 'エンタープライズダッシュボード、ナイトオーディット、高度な分析' },
      { segment: 'ホテルチェーン（マルチプロパティ）', fit: 'excellent', note: 'チェーンダッシュボード、クロスプロパティ分析、一元料金' },
      { segment: 'ブティック＆ライフスタイル', fit: 'strong', note: 'ゲストCRM、パーソナライズ通信、ロイヤルティプログラム' },
      { segment: 'リゾート＆スパ', fit: 'strong', note: 'POS、F&B管理、アクティビティ予約、グループ予約' },
      { segment: 'ホステル＆バジェット', fit: 'good', note: 'ルームボード、基本PMS、OTAチャネル同期' },
      { segment: 'B&B＆バケーションレンタル', fit: 'good', note: '予約エンジン、ゲストメッセージ、請求書管理' },
      { segment: 'インド市場', fit: 'excellent', note: 'GST準拠、C-Form、FRRO、UPI決済' },
      { segment: 'グローバル市場', fit: 'excellent', note: '6以上の通貨、多言語、GDPR、200以上のOTAチャネル、GDS' },
    ],
  },
  zh: {
    badge: 'HotelStack适合谁？',
    title: '目标市场契合度',
    subtitle: '从精品民宿到全球连锁酒店 — HotelStack适应每种物业类型和市场。',
    headers: { segment: '市场细分', fitLevel: '契合度', capabilities: '核心能力' },
    fitLabels: { excellent: '非常适合', strong: '强力适配', good: '良好适配' },
    segments: [
      { segment: '小型酒店（1–20间客房）', fit: 'excellent', note: '自助入住、简化PMS、实惠定价' },
      { segment: '中型酒店（21–100间客房）', fit: 'excellent', note: '完整PMS套件、渠道管理器、收益工具' },
      { segment: '大型酒店（100+间客房）', fit: 'excellent', note: '企业仪表板、夜审、高级分析' },
      { segment: '连锁酒店（多物业）', fit: 'excellent', note: '连锁仪表板、跨物业分析、统一费率' },
      { segment: '精品和生活方式酒店', fit: 'strong', note: '客户CRM、个性化沟通、忠诚度计划' },
      { segment: '度假村和水疗中心', fit: 'strong', note: 'POS、餐饮管理、活动预订、团体预订' },
      { segment: '旅舍和经济型', fit: 'good', note: '房间看板、基础PMS、OTA渠道同步' },
      { segment: '民宿和度假租赁', fit: 'good', note: '预订引擎、客户消息、发票管理' },
      { segment: '印度市场', fit: 'excellent', note: 'GST合规、C-Form、FRRO、UPI支付' },
      { segment: '全球市场', fit: 'excellent', note: '6+货币、多语言、GDPR、200+OTA渠道、GDS' },
    ],
  },
  hi: {
    badge: 'HotelStack किसके लिए है?',
    title: 'लक्ष्य बाज़ार उपयुक्तता',
    subtitle: 'बुटीक B&B से लेकर वैश्विक होटल चेन तक — HotelStack हर प्रकार की संपत्ति और बाज़ार के लिए अनुकूल है।',
    headers: { segment: 'बाज़ार खंड', fitLevel: 'उपयुक्तता स्तर', capabilities: 'प्रमुख क्षमताएं' },
    fitLabels: { excellent: 'उत्कृष्ट', strong: 'मज़बूत', good: 'अच्छा' },
    segments: [
      { segment: 'छोटे होटल (1–20 कमरे)', fit: 'excellent', note: 'कियोस्क चेक-इन, सरल PMS, किफ़ायती मूल्य' },
      { segment: 'मध्यम होटल (21–100 कमरे)', fit: 'excellent', note: 'पूर्ण PMS सूट, चैनल मैनेजर, राजस्व उपकरण' },
      { segment: 'बड़े होटल (100+ कमरे)', fit: 'excellent', note: 'एंटरप्राइज़ डैशबोर्ड, नाइट ऑडिट, उन्नत विश्लेषण' },
      { segment: 'होटल चेन (बहु-संपत्ति)', fit: 'excellent', note: 'चेन डैशबोर्ड, क्रॉस-प्रॉपर्टी एनालिटिक्स, केंद्रीकृत दरें' },
      { segment: 'बुटीक और लाइफ़स्टाइल', fit: 'strong', note: 'गेस्ट CRM, व्यक्तिगत संचार, लॉयल्टी प्रोग्राम' },
      { segment: 'रिसॉर्ट और स्पा', fit: 'strong', note: 'POS, F&B प्रबंधन, गतिविधि बुकिंग, समूह आरक्षण' },
      { segment: 'हॉस्टल और बजट', fit: 'good', note: 'रूम बोर्ड, बेसिक PMS, OTA चैनल सिंक' },
      { segment: 'B&B और वेकेशन रेंटल', fit: 'good', note: 'बुकिंग इंजन, गेस्ट मैसेजिंग, इनवॉइस प्रबंधन' },
      { segment: 'भारतीय बाज़ार', fit: 'excellent', note: 'GST अनुपालन, C-Form, FRRO, UPI भुगतान' },
      { segment: 'वैश्विक बाज़ार', fit: 'excellent', note: '6+ मुद्राएं, बहुभाषी, GDPR, 200+ OTA चैनल, GDS' },
    ],
  },
};
